import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import './LegalPages.css';

/**
 * TermsAndConditions Component
 * 
 * Legal page defining usage rules for Cresta.
 * Explicitly states FREE BETA status and no liability for data loss.
 * Complies with Colombia's legal requirements.
 */
function TermsAndConditions() {
    const currentYear = new Date().getFullYear();

    return (
        <div className="legal-page">
            <div className="legal-container">
                {/* Header */}
                <div className="legal-header">
                    <div className="legal-icon">
                        <Shield size={48} className="icon-emerald" />
                    </div>
                    <h1 className="legal-title">Términos y Condiciones de Servicio</h1>
                    <p className="legal-subtitle">
                        Última actualización: {currentYear}
                    </p>
                </div>

                {/* Beta Notice - Prominent */}
                <div className="legal-notice beta-notice">
                    <h3>💎 Fase BETA con Planes Premium</h3>
                    <p>
                        <strong>Cresta</strong> se encuentra actualmente en fase <strong>BETA GRATUITA</strong>.
                        El servicio se proporciona "tal cual" (<em>as-is</em>) sin garantías de ningún tipo.
                        Durante esta fase, <strong>no asumimos responsabilidad por pérdida de datos,
                            interrupciones del servicio o cualquier daño derivado del uso de la plataforma</strong>.
                    </p>
                    <p>
                        Al utilizar Cresta, aceptas que comprendes y asumes estos riesgos inherentes a una plataforma en desarrollo.
                    </p>
                </div>

                {/* Content */}
                <div className="legal-content">
                    <section className="legal-section">
                        <h2>1. Aceptación de los Términos</h2>
                        <p>
                            Al acceder y utilizar Cresta (en adelante, "la Plataforma"), aceptas estos Términos de
                            Servicio en su totalidad. Si no estás de acuerdo, no utilices la Plataforma.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>2. Descripción del Servicio</h2>
                        <p>
                            Cresta es un software de gestión para salones de belleza que permite:
                        </p>
                        <ul>
                            <li>Agendamiento de citas y gestión de clientes</li>
                            <li>Administración de empleados y servicios</li>
                            <li>Control de inventario</li>
                            <li>Reportes financieros y Facturación Electrónica (vía terceros)</li>
                            <li>Notificaciones automatizadas (WhatsApp/Email)</li>
                            <li>Procesamiento de pagos (Wompi)</li>
                        </ul>
                        <p>
                            El servicio se presta de manera remota a través de internet (SaaS - Software as a Service).
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>3. Cuenta de Usuario</h2>
                        <p>
                            Para utilizar la Plataforma, debes crear una cuenta proporcionando una dirección de correo
                            electrónico válida. Eres responsable de:
                        </p>
                        <ul>
                            <li>Mantener la confidencialidad de tus credenciales de acceso</li>
                            <li>Todas las actividades realizadas bajo tu cuenta</li>
                            <li>Notificarnos inmediatamente de cualquier uso no autorizado</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>4. Uso Aceptable</h2>
                        <p>Te comprometes a NO utilizar la Plataforma para:</p>
                        <ul>
                            <li>Actividades ilegales o fraudulentas</li>
                            <li>Violar derechos de terceros</li>
                            <li>Transmitir virus, malware o código malicioso</li>
                            <li>Realizar ingeniería inversa o intentar acceder a sistemas no autorizados</li>
                            <li>Revender o redistribuir el servicio sin autorización expresa</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>5. Propiedad Intelectual</h2>
                        <p>
                            Cresta y todo su contenido (código, diseño, marca, logos) son propiedad de
                            <strong> CottonTopTech</strong>. Los datos que ingresas a la Plataforma
                            (clientes, servicios, citas) siguen siendo de tu propiedad.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>6. Suspensión y Terminación</h2>
                        <p>
                            Nos reservamos el derecho de suspender o terminar tu acceso a la Plataforma
                            en caso de incumplimiento de estos Términos, sin previo aviso. Tú puedes
                            cerrar tu cuenta en cualquier momento desde la configuración.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>7. Limitación de Responsabilidad</h2>
                        <p>
                            <strong>Durante la fase BETA:</strong>
                        </p>
                        <ul>
                            <li>El servicio se presta de forma gratuita y experimental</li>
                            <li>No garantizamos disponibilidad ininterrumpida (uptime)</li>
                            <li>No nos hacemos responsables por pérdida de datos o información</li>
                            <li>No garantizamos que el servicio esté libre de errores</li>
                        </ul>
                        <p>
                            <strong>CRESTA SE PROPORCIONA "TAL CUAL" SIN GARANTÍAS DE NINGÚN TIPO,
                                YA SEAN EXPRESAS O IMPLÍCITAS.</strong>
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>8. Modificaciones</h2>
                        <p>
                            Nos reservamos el derecho de modificar estos Términos en cualquier momento.
                            Los cambios entrarán en vigor inmediatamente después de su publicación.
                            El uso continuado de la Plataforma constituye tu aceptación de los nuevos términos.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>9. Ley Aplicable y Jurisdicción</h2>
                        <p>
                            Estos Términos se rigen por las leyes de la República de Colombia.
                            Cualquier disputa será resuelta ante los tribunales competentes de Cali, Valle del Cauca.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>10. Contacto</h2>
                        <p>
                            Para preguntas sobre estos Términos, contáctanos en:{' '}
                            <a href="mailto:info@cottontoptech.com" className="legal-link">info@cottontoptech.com</a>
                        </p>
                    </section>
                </div>

                {/* Footer Actions */}
                <div className="legal-footer">
                    <Link to="/" className="btn-secondary">
                        Volver al Inicio
                    </Link>
                    <Link to="/privacy" className="btn-link">
                        Ver Política de Privacidad
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default TermsAndConditions;
